import type { ScanStatus, Modality, AssetType } from '@/api/types'

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral'

interface StatusBadgeProps {
  label: string
  variant?: BadgeVariant
}

const variantClasses: Record<BadgeVariant, string> = {
  success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  // tertiary (#C22221) tint
  error:   'bg-error/10 text-error dark:bg-error/20 dark:text-red-300',
  // secondary (#94C8E4) tint — primary text in light, secondary in dark (pops)
  info:    'bg-secondary/20 text-primary dark:bg-secondary/15 dark:text-secondary',
  // tonal container surface
  neutral: 'bg-container-light text-subtle-light dark:bg-subtle-dark/15 dark:text-subtle-dark',
}

export function StatusBadge({ label, variant = 'neutral' }: StatusBadgeProps) {
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${variantClasses[variant]}`}>
      {label}
    </span>
  )
}

export function ScanStatusBadge({ status }: { status: ScanStatus }) {
  const map: Record<ScanStatus, { variant: BadgeVariant; icon: string }> = {
    pending: { variant: 'neutral', icon: 'schedule' },
    running: { variant: 'info', icon: '' },
    completed: { variant: 'success', icon: 'check_circle' },
    failed: { variant: 'error', icon: 'error' },
  }
  // The status comes from the API, so an unrecognised value is possible;
  // destructuring map[status] directly would throw and blank the whole page.
  const { variant, icon } = map[status] ?? { variant: 'neutral' as BadgeVariant, icon: 'help_outline' }
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${variantClasses[variant]}`}
    >
      {status === 'running' ? (
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
        </span>
      ) : (
        <span className="material-icons text-xs">{icon}</span>
      )}
      {status}
    </span>
  )
}

const MODALITY_CLASSES: Record<Modality, string> = {
  source:  'bg-sky-100     text-sky-700     dark:bg-sky-900/40     dark:text-sky-300',
  binary:  'bg-violet-100  text-violet-700  dark:bg-violet-900/40  dark:text-violet-300',
  runtime: 'bg-amber-100   text-amber-700   dark:bg-amber-900/40   dark:text-amber-300',
  network: 'bg-teal-100    text-teal-700    dark:bg-teal-900/40    dark:text-teal-300',
  infra:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  manual:  'bg-slate-100   text-slate-600   dark:bg-slate-700/50   dark:text-slate-300',
}

const MODALITY_FALLBACK = 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300'

export function ModalityBadge({ modality }: { modality: Modality }) {
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${MODALITY_CLASSES[modality] ?? MODALITY_FALLBACK}`}>
      {modality}
    </span>
  )
}

export function AssetTypeBadge({ assetType }: { assetType: string }) {
  const map: Record<AssetType, BadgeVariant> = {
    algorithm: 'info',
    certificate: 'success',
    key: 'warning',
    protocol: 'neutral',
    kdf: 'info',
  }
  return <StatusBadge label={assetType} variant={(map as Record<string, BadgeVariant>)[assetType] ?? 'neutral'} />
}

export function QuantumSafeBadge({ isSafe }: { isSafe: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
        isSafe
          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
          : 'bg-error/10 text-error dark:bg-error/20 dark:text-red-300'
      }`}
    >
      <span className="material-icons text-xs">{isSafe ? 'shield' : 'warning'}</span>
      {isSafe ? 'Quantum Safe' : 'At Risk'}
    </span>
  )
}
